import type HighchartsType from 'highcharts/highstock';

import { createFullScreenYAxisOptions } from './fullScreenChartAxis';
import { getInitialRangeButtonIndex, toHighchartsSeries } from './fullScreenChartSeries';
import { createFullScreenRolloverTooltip } from './fullScreenChartTooltip';
import { applyFullscreenWatermarkLayout } from './fullScreenChartWatermark';

import type {
  CreateFullScreenStockOptionsParams,
  FullscreenWatermarkChart
} from './fullScreenChartTypes';

const RANGE_SELECTOR_BUTTONS: HighchartsType.RangeSelectorButtonsOptions[] = [
  { type: 'month', count: 1, text: '1m' },
  { type: 'month', count: 3, text: '3m' },
  { type: 'month', count: 6, text: '6m' },
  { type: 'year', count: 1, text: '1y' },
  { type: 'ytd', text: 'YTD' },
  { type: 'all', text: 'All' }
];

const STOCK_TOOLS_BUTTONS = [
  'indicators',
  'simpleShapes',
  'lines',
  'crookedLines',
  'measure',
  'advanced',
  'toggleAnnotations',
  'verticalLabels',
  'flags',
  'zoomChange',
  'currentPriceIndicator'
] as const;

export const withoutInitialRangeSelection = (
  options: HighchartsType.Options
): HighchartsType.Options => ({
  ...options,
  rangeSelector: { ...options.rangeSelector, selected: undefined }
});

export const createFullScreenStockOptions = ({
  chartSeries,
  hiddenSeriesIds,
  tooltip = createFullScreenRolloverTooltip(() => ''),
  useSmallWatermark = false
}: CreateFullScreenStockOptionsParams): HighchartsType.Options => ({
  accessibility: { enabled: false },
  chart: {
    allowMutatingData: false,
    animation: false,
    events: {
      load() {
        applyFullscreenWatermarkLayout(this as FullscreenWatermarkChart, useSmallWatermark);
      },
      redraw() {
        applyFullscreenWatermarkLayout(this as FullscreenWatermarkChart, useSmallWatermark);
      }
    },
    height: null,
    reflow: true,
    style: { fontFamily: 'Cairo, sans-serif' },
    type: 'line',
    zooming: { type: 'x' }
  },
  credits: { enabled: false },
  exporting: {
    buttons: { contextButton: { enabled: false } },
    enabled: true,
    fallbackToExportServer: false
  },
  legend: { enabled: false },
  lang: { rangeSelectorZoom: '' },
  navigator: { adaptToUpdatedData: false, enabled: true },
  plotOptions: {
    line: { states: { hover: { lineWidth: 3 } } },
    series: {
      animation: false,
      states: { hover: { enabled: false }, inactive: { opacity: 1 } }
    }
  },
  rangeSelector: {
    allButtonsEnabled: true,
    buttonPosition: { align: 'left', x: 0, y: 0 },
    buttons: RANGE_SELECTOR_BUTTONS,
    buttonSpacing: 8,
    buttonTheme: {
      fill: '#121836',
      height: 22,
      r: 0,
      style: { color: '#FFF' },
      states: {
        disabled: {
          fill: 'rgba(255, 255, 255, 0.12)',
          style: { color: 'rgba(255, 255, 255, 0.3)' }
        },
        hover: { style: { color: '#A1A6AB' } },
        select: { fill: '#E84E0F', style: { color: '#FFF' } }
      },
      width: 40
    },
    dropdown: 'never',
    inputBoxHeight: 24,
    inputDateFormat: '%Y-%m-%d',
    inputEditDateFormat: '%Y-%m-%d',
    inputEnabled: false,
    inputStyle: { fontSize: '12px' },
    labelStyle: { display: 'none', fontSize: '12px' },
    selected: getInitialRangeButtonIndex(chartSeries)
  },
  scrollbar: { enabled: true },
  series: chartSeries.map((series, index) => toHighchartsSeries(series, index, hiddenSeriesIds)),
  stockTools: { gui: { buttons: [...STOCK_TOOLS_BUTTONS], enabled: true } },
  title: { text: undefined },
  tooltip,
  xAxis: {
    crosshair: true,
    labels: { style: { fontSize: '10px' } },
    ordinal: false,
    type: 'datetime'
  },
  yAxis: createFullScreenYAxisOptions()
});
